import { ITranscriptionService } from "./interfaces/ITranscriptionService";
import { TranscriptionServiceFactory } from "./TranscriptionServiceFactory";
import { DirectModeAuth, assertValidAuthConfig } from "./auth";
import {
  createTelemetryIntegrationHub,
  telemetryToLegacyMetricsSnapshot,
} from "./metrics";
import type {
  TelemetryIntegrationDispatchReason,
  TelemetryIntegrationHub,
  TelemetryIntegrationWarning,
  TelemetryRow,
  TelemetrySnapshot,
  TranscriptionMetricsSnapshot,
} from "./metrics";
import { buildSessionReport } from "./audit";
import {
  BatchReprocessStatus,
  createUnsupportedBatchReprocessStatus,
} from "./batchReprocess";
import type {
  DebugAuditCheckpoint,
  DebugAuditConfig,
  DebugAuditFinalDigest,
  DebugAuditFile,
  DebugAuditState,
  SessionReportIncident,
  SessionReportPhase,
  SessionReportResilienceStatus,
  SessionReportTranscriptMilestone,
} from "./audit";
import { createDebugAuditStore } from "./audit/DebugAuditStore";
import type { SttAuditIngestionWarning } from "./sttAuditIngestion";
import { authService } from "../../api/api";
import EventEmitter from "../../shared/EventEmitter";
import { Connection, BaseConfig, Providers } from "../../SofyaTranscriber";

const DEBUG_CHECKPOINT_INTERVAL_MS = 5000;
const RESILIENCE_STATUS_LOG_INTERVAL_MS = 5000;
const LIVE_AUDIT_PHASE_LIMIT = 256;
const LIVE_AUDIT_INCIDENT_LIMIT = 512;
const LIVE_AUDIT_MILESTONE_LIMIT = 64;
const LIVE_AUDIT_CHECKPOINT_LIMIT = 720;
const LIVE_AUDIT_DIGEST_LIMIT = 256;
type TranscriptionServiceEventName =
  | "recognizing"
  | "recognized"
  | "recognized_diarization"
  | "error"
  | "stopped"
  | "connected"
  | "disconnected"
  | "reconnecting"
  | "reconnected"
  | "resilience_status"
  | "telemetry"
  | "telemetry_row"
  | "batch_reprocess_status"
  | "stt_session";

type TranscriptionServiceListenerEntry = {
  eventName: TranscriptionServiceEventName;
  listener: (detail?: unknown) => void;
};

type LiveDebugAuditState = DebugAuditConfig & {
  sessionId: string;
  createdAt: number;
  startedAt: number | null;
  stoppedAt: number | null;
  requestedProvider: DebugAuditState["requestedProvider"];
  resolvedProvider: DebugAuditState["resolvedProvider"];
  phases: SessionReportPhase[];
  incidents: SessionReportIncident[];
  transcriptMilestones: SessionReportTranscriptMilestone[];
  checkpoints: DebugAuditCheckpoint[];
  finalDigests: DebugAuditFinalDigest[];
  lastResilienceStatus: SessionReportResilienceStatus | null;
  lastMetricsSnapshot: TranscriptionMetricsSnapshot | null;
  lastCheckpointAt: number | null;
  phaseSequence: number;
  incidentSequence: number;
  checkpointSequence: number;
  finalDigestSequence: number;
  milestones: {
    firstPartial: SessionReportTranscriptMilestone | null;
    firstFinal: SessionReportTranscriptMilestone | null;
    lastFinal: SessionReportTranscriptMilestone | null;
    firstDiarization: SessionReportTranscriptMilestone | null;
  };
};

const cloneResilienceStatus = (
  status: SessionReportResilienceStatus | null
): SessionReportResilienceStatus | null =>
  status ? ({ ...status } as SessionReportResilienceStatus) : null;

const cloneMetricsSnapshot = (
  snapshot: TranscriptionMetricsSnapshot | null
): TranscriptionMetricsSnapshot | null =>
  snapshot
    ? (JSON.parse(JSON.stringify(snapshot)) as TranscriptionMetricsSnapshot)
    : null;

const normalizeResilienceStatus = (
  detail: Record<string, unknown>,
  timestamp: number
): SessionReportResilienceStatus => ({
  ...(detail as SessionReportResilienceStatus),
  timestamp,
});

export class SofyaTranscriber extends EventEmitter {
  private transcriptionService: ITranscriptionService | null = null;
  private debugAudit: LiveDebugAuditState | null;
  private readonly telemetryIntegration: TelemetryIntegrationHub;
  private readonly debugAuditStore = createDebugAuditStore();
  private debugAuditWritePromise: Promise<void> = Promise.resolve();
  private transcriptionServiceListeners: TranscriptionServiceListenerEntry[] = [];
  private resilienceStatusLogTimer: ReturnType<typeof setInterval> | null = null;

  constructor(connection: Connection) {
    super();
    const config = "config" in connection ? connection.config : undefined;
    // Direct-endpoint mode: falha de forma síncrona quando a credencial
    // configurada não pode ser usada, antes de qualquer tentativa de conexão.
    if ("endpoint" in connection) {
      const directConfig = config as
        | { auth?: DirectModeAuth; mode?: string; token?: string }
        | undefined;
      assertValidAuthConfig(directConfig?.auth, directConfig?.mode);

      if (directConfig?.token) {
        console.warn(
          '[sofya.transcription] config.token is deprecated and will be removed in 1.0.0. ' +
            'Use auth: { type: "jwt", token } instead (pass the raw JWT, without "Bearer ").'
        );
      }
    }

    this.telemetryIntegration = this.createTelemetryIntegration(config);
    this.debugAudit = this.createDebugAuditState(connection);

    if (this.debugAudit) {
      this.initializeDebugAuditSession(this.debugAudit);
    }

    this.initializeTranscriptionService(connection);
  }

  startTranscription(mediaStream: MediaStream) {
    if (!this.transcriptionService) {
      throw new Error("Transcription Service Not Ready. Try again.");
    }

    this.addTranscriptionListeners();
    this.prepareDebugSessionForStart();
    this.markDebugStart();
    this.transcriptionService.startTranscription(mediaStream);
    this.startResilienceStatusLogging();
  }

  async stopTranscription(): Promise<unknown> {
    if (!this.transcriptionService) {
      throw new Error("Transcription Service Not Ready. Try again.");
    }

    let stopResult: unknown = null;
    try {
      stopResult = await this.transcriptionService.stopTranscription();
    } finally {
      this.markDebugStop();
      this.stopResilienceStatusLogging();
      await this.dispatchTelemetryIntegration("stop");
      await this.dispatchSttAuditIngestion();
      if (this.debugAudit?.autoDownload) {
        await this.downloadDebugAudit();
      }
      this.detachTranscriptionListeners();
    }

    return stopResult;
  }

  async reprocessAudio<TPayload = unknown>(): Promise<TPayload> {
    if (!this.transcriptionService?.reprocessAudio) {
      throw new Error("Audio reprocessing is not supported by the active provider.");
    }

    return this.transcriptionService.reprocessAudio<TPayload>();
  }

  getBatchReprocessStatus(): BatchReprocessStatus {
    if (!this.transcriptionService?.getBatchReprocessStatus) {
      return createUnsupportedBatchReprocessStatus();
    }

    return this.transcriptionService.getBatchReprocessStatus();
  }

  getBatchReprocessRemainingTime(): number | null {
    if (!this.transcriptionService?.getBatchReprocessRemainingTime) {
      return null;
    }

    return this.transcriptionService.getBatchReprocessRemainingTime();
  }

  pauseTranscription() {
    if (!this.transcriptionService) {
      throw new Error("Transcription Service Not Ready. Try again.");
    }
    this.transcriptionService.pauseTranscription();
  }

  resumeTranscription() {
    if (!this.transcriptionService) {
      throw new Error("Transcription Service Not Ready. Try again.");
    }

    this.transcriptionService.resumeTranscription();
  }

  getResilienceStatus() {
    if (!this.transcriptionService?.getResilienceStatus) {
      return null;
    }

    return this.transcriptionService.getResilienceStatus();
  }

  getSttSessionId(): string | null {
    if (!this.transcriptionService?.getSttSessionId) {
      return null;
    }

    return this.transcriptionService.getSttSessionId();
  }

  getSttSessionIds(): string[] {
    if (!this.transcriptionService?.getSttSessionIds) {
      return [];
    }

    return this.transcriptionService.getSttSessionIds();
  }

  getTelemetrySnapshot(): TelemetrySnapshot | null {
    if (!this.transcriptionService?.getTelemetrySnapshot) {
      return null;
    }

    return this.transcriptionService.getTelemetrySnapshot();
  }

  getTelemetryRows(): TelemetryRow[] {
    if (!this.transcriptionService?.getTelemetryRows) {
      return [];
    }

    return this.transcriptionService.getTelemetryRows();
  }

  clearTelemetryRows() {
    if (!this.transcriptionService?.clearTelemetryRows) {
      return;
    }

    this.transcriptionService.clearTelemetryRows();
  }

  resetTelemetry() {
    if (!this.transcriptionService?.resetTelemetry) {
      return;
    }

    this.transcriptionService.resetTelemetry();
  }

  async getDebugAudit(): Promise<DebugAuditFile | null> {
    if (!this.debugAudit) {
      return null;
    }

    await this.flushDebugAuditWrites();

    const storedAudit = await this.debugAuditStore.getSessionSnapshot(
      this.debugAudit.sessionId
    );
    const activeAuditState = storedAudit ?? this.buildInMemoryDebugAuditState();
    if (!activeAuditState) {
      return null;
    }

    const now = Date.now();
    const currentStatus = this.resolveCurrentDebugStatus();
    const serviceTelemetry = this.transcriptionService?.getTelemetrySnapshot?.() ?? null;
    const serviceMetrics = serviceTelemetry
      ? telemetryToLegacyMetricsSnapshot(serviceTelemetry)
      : null;
    const currentMetrics =
      serviceMetrics?.updatedAt && this.debugAudit.lastMetricsSnapshot?.updatedAt
        ? serviceMetrics.updatedAt >= this.debugAudit.lastMetricsSnapshot.updatedAt
          ? serviceMetrics
          : this.debugAudit.lastMetricsSnapshot
        : serviceMetrics ?? this.debugAudit.lastMetricsSnapshot ?? null;
    const startedAt = activeAuditState.startedAt;
    const stoppedAt = activeAuditState.stoppedAt;
    const { report } = buildSessionReport({
      auditState: activeAuditState,
      resilienceStatus: currentStatus,
      metricsSnapshot: currentMetrics,
      now,
    });

    return {
      schemaVersion: 2,
      kind: "sofya-transcriber-debug-audit",
      sessionId: activeAuditState.sessionId,
      label: activeAuditState.label,
      generatedAt: new Date(now).toISOString(),
      createdAt: new Date(activeAuditState.createdAt).toISOString(),
      startedAt: startedAt ? new Date(startedAt).toISOString() : null,
      stoppedAt: stoppedAt ? new Date(stoppedAt).toISOString() : null,
      sessionDurationMs:
        startedAt ? Math.max((stoppedAt ?? now) - startedAt, 0) : null,
      requestedProvider: activeAuditState.requestedProvider,
      resolvedProvider: activeAuditState.resolvedProvider,
      sttSessionIds: this.getSttSessionIds(),
      metadata: activeAuditState.metadata,
      environment: {
        userAgent:
          typeof navigator === "undefined" ? null : navigator.userAgent ?? null,
        locationHref:
          typeof window === "undefined" ? null : window.location?.href ?? null,
      },
      report,
    };
  }

  async downloadDebugAudit(fileName?: string): Promise<string | null> {
    const audit = await this.getDebugAudit();
    if (
      !audit ||
      typeof document === "undefined" ||
      typeof URL?.createObjectURL !== "function"
    ) {
      return null;
    }

    const downloadName = this.getDebugFileName(fileName);
    const payload = JSON.stringify(audit, null, 2);
    const blob = new Blob([payload], {
      type: "application/json",
    });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = objectUrl;
    link.download = downloadName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);

    await this.clearDebugAudit();

    return downloadName;
  }

  async clearDebugAudit(): Promise<void> {
    if (!this.debugAudit) {
      return;
    }

    const previousSessionId = this.debugAudit.sessionId;
    await this.flushDebugAuditWrites();

    try {
      await this.debugAuditStore.clear(previousSessionId);
    } catch (error) {
      console.warn("Failed to clear debug audit persistence.", error);
    }

    this.resetDebugAuditSession(previousSessionId, false);
  }

  private async initializeTranscriptionService(
    connection: Connection
  ): Promise<void> {
    if ("apiKey" in connection) {
      await this.getProvider(connection.apiKey, connection.config);
      return;
    }

    if ("endpoint" in connection) {
      try {
        const endpointConfig = {
          mode: "mode" in connection ? connection.mode : undefined,
          ...connection.config,
          endpoint: { endpoint: connection.endpoint },
        };
        this.transcriptionService = await TranscriptionServiceFactory.create(
          connection.provider,
          endpointConfig
        );

        this.setResolvedDebugProvider(connection.provider);
        this.addTranscriptionListeners();
        this.emitPublicEvent("ready");
        return;
      } catch (endpointError) {
        console.warn(`Failed to connect to endpoint: `, endpointError);
        this.emitPublicEvent("error", endpointError);
      }
    }

    this.emitPublicEvent(
      "error",
      new Error("Failed to initialize the transcription service.")
    );
  }

  private async getProvider(apiKey: string, config?: BaseConfig) {
    try {
      const providersInfo = await this.authenticate(apiKey);
      const providers = providersInfo.providers;
      for (const providerInfo of providers) {
        const { provider, additional_info } = providerInfo;

        if (additional_info.endpoints && additional_info.endpoints.length > 0) {
          let connected = false;
          for (const endpoint of additional_info.endpoints) {
            try {
              const endpointConfig = {
                ...config,
                ...additional_info,
                endpoint,
              };
              this.transcriptionService =
                await TranscriptionServiceFactory.create(
                  provider,
                  endpointConfig
                );

              this.setResolvedDebugProvider(provider);
              this.addTranscriptionListeners();
              connected = true;
              this.emitPublicEvent("ready");
              return;
            } catch (endpointError) {
              console.warn(`Failed to connect to endpoint: `, endpointError);
            }
          }

          if (!connected) {
            continue;
          }
        } else {
          try {
            const providerConfig = { ...config, ...additional_info };
            this.transcriptionService =
              await TranscriptionServiceFactory.create(
                provider,
                providerConfig
              );

            this.setResolvedDebugProvider(provider);
            this.addTranscriptionListeners();
            this.emitPublicEvent("ready");
            return;
          } catch (providerError) {
            console.warn(`Failed to initialize provider: `, providerError);
          }
        }
      }

      throw new Error("Failed to connect to any provider.");
    } catch (error) {
      console.error("Error during initialization:", error);
      this.emitPublicEvent("error", error);
    }
  }

  private addTranscriptionListeners() {
    if (!this.transcriptionService) {
      return;
    }

    this.detachTranscriptionListeners();

    this.bindTranscriptionListener("recognizing", (text) => {
      this.emitPublicEvent("recognizing", text);
    });
    this.bindTranscriptionListener("recognized", (text) => {
      this.emitPublicEvent("recognized", text);
    });
    this.bindTranscriptionListener("recognized_diarization", (diarization) => {
      this.emitPublicEvent("recognized_diarization", diarization);
    });
    this.bindTranscriptionListener("error", (error) => {
      this.emitPublicEvent("error", error);
    });
    this.bindTranscriptionListener("stopped", () => {
      this.emitPublicEvent("stopped");
    });
    this.bindTranscriptionListener("connected", () => {
      this.emitPublicEvent("connected");
    });
    this.bindTranscriptionListener("disconnected", (details) => {
      this.emitPublicEvent("disconnected", details);
    });
    this.bindTranscriptionListener("reconnecting", (details) => {
      this.emitPublicEvent("reconnecting", details);
    });
    this.bindTranscriptionListener("reconnected", () => {
      this.emitPublicEvent("reconnected");
    });
    this.bindTranscriptionListener("resilience_status", (status) => {
      this.emitPublicEvent("resilience_status", status);
    });
    this.bindTranscriptionListener("telemetry", (telemetry) => {
      this.emitPublicEvent("telemetry", telemetry);
    });
    this.bindTranscriptionListener("telemetry_row", (row) => {
      this.emitPublicEvent("telemetry_row", row);
    });
    this.bindTranscriptionListener("batch_reprocess_status", (status) => {
      this.emitPublicEvent("batch_reprocess_status", status);
    });
    this.bindTranscriptionListener("stt_session", (info) => {
      this.emitPublicEvent("stt_session", info);
    });

    const currentStatus = this.transcriptionService.getResilienceStatus?.();
    if (currentStatus) {
      this.emitPublicEvent("resilience_status", currentStatus);
    }
    const currentTelemetry = this.transcriptionService.getTelemetrySnapshot?.();
    if (currentTelemetry) {
      this.emitPublicEvent("telemetry", currentTelemetry);
    }
    const currentBatchStatus = this.transcriptionService.getBatchReprocessStatus?.();
    if (currentBatchStatus) {
      this.emitPublicEvent("batch_reprocess_status", currentBatchStatus);
    }
  }

  private startResilienceStatusLogging() {
    if (this.resilienceStatusLogTimer !== null) {
      return;
    }

    this.resilienceStatusLogTimer = setInterval(() => {
      console.log("[SofyaResilienceStatus]", this.getResilienceStatus());
    }, RESILIENCE_STATUS_LOG_INTERVAL_MS);
  }

  private stopResilienceStatusLogging() {
    if (this.resilienceStatusLogTimer === null) {
      return;
    }

    clearInterval(this.resilienceStatusLogTimer);
    this.resilienceStatusLogTimer = null;
  }

  private bindTranscriptionListener(
    eventName: TranscriptionServiceEventName,
    listener: (detail?: unknown) => void
  ) {
    if (!this.transcriptionService) {
      return;
    }

    // @ts-ignore
    this.transcriptionService.on(eventName, listener);
    this.transcriptionServiceListeners.push({ eventName, listener });
  }

  private detachTranscriptionListeners() {
    if (!this.transcriptionService || this.transcriptionServiceListeners.length === 0) {
      this.transcriptionServiceListeners = [];
      return;
    }

    const service = this.transcriptionService as {
      off?: (eventName: string, listener: (detail?: unknown) => void) => void;
      removeListener?: (
        eventName: string,
        listener: (detail?: unknown) => void
      ) => void;
    };

    this.transcriptionServiceListeners.forEach(({ eventName, listener }) => {
      if (typeof service.off === "function") {
        service.off(eventName, listener);
        return;
      }

      if (typeof service.removeListener === "function") {
        service.removeListener(eventName, listener);
      }
    });
    this.transcriptionServiceListeners = [];
  }

  private async authenticate(
    apiKey: string
  ): Promise<{ providers: { provider: Providers; additional_info: any }[] }> {
    try {
      const authData = await authService(apiKey);
      if (authData?.providers && Array.isArray(authData.providers)) {
        return authData;
      }
      throw new Error("Invalid authentication data format.");
    } catch {
      throw new Error("Could not authenticate. Validate your apiKey");
    }
  }

  private createDebugAuditState(connection: Connection): LiveDebugAuditState {
    const config = "config" in connection ? connection.config : undefined;
    const normalized = this.normalizeDebugAuditConfig(config);

    return this.buildLiveDebugAuditState(normalized, {
      requestedProvider: "apiKey" in connection ? "apiKey" : connection.provider,
      resolvedProvider: "apiKey" in connection ? null : connection.provider,
    });
  }

  private buildLiveDebugAuditState(
    config: DebugAuditConfig,
    providers: {
      requestedProvider: DebugAuditState["requestedProvider"];
      resolvedProvider: DebugAuditState["resolvedProvider"];
    }
  ): LiveDebugAuditState {
    return {
      ...config,
      sessionId: this.generateDebugSessionId(),
      createdAt: Date.now(),
      startedAt: null,
      stoppedAt: null,
      requestedProvider: providers.requestedProvider,
      resolvedProvider: providers.resolvedProvider,
      phases: [],
      incidents: [],
      transcriptMilestones: [],
      checkpoints: [],
      finalDigests: [],
      lastResilienceStatus: null,
      lastMetricsSnapshot: null,
      lastCheckpointAt: null,
      phaseSequence: 0,
      incidentSequence: 0,
      checkpointSequence: 0,
      finalDigestSequence: 0,
      milestones: {
        firstPartial: null,
        firstFinal: null,
        lastFinal: null,
        firstDiarization: null,
      },
    };
  }

  private normalizeDebugAuditConfig(
    config?: BaseConfig
  ): DebugAuditConfig {
    const debugConfig = config?.debug;

    if (!debugConfig) {
      return {
        enabled: false,
        advancedMetrics: false,
        autoDownload: false,
        fileName: null,
        label: null,
        metadata: null,
      };
    }

    if (debugConfig === true) {
      return {
        enabled: true,
        advancedMetrics: true,
        autoDownload: false,
        fileName: null,
        label: null,
        metadata: null,
      };
    }

    return {
      enabled: debugConfig.enabled !== false,
      advancedMetrics:
        debugConfig.enabled === false
          ? false
          : (debugConfig as { advancedMetrics?: boolean }).advancedMetrics ?? true,
      autoDownload: Boolean(debugConfig.autoDownload),
      fileName: debugConfig.fileName ?? null,
      label: debugConfig.label ?? null,
      metadata: debugConfig.metadata
        ? this.serializeDebugDetail(debugConfig.metadata)
        : null,
    };
  }

  private createTelemetryIntegration(config?: BaseConfig) {
    return createTelemetryIntegrationHub(config?.telemetry, (warning) => {
      this.handleTelemetryIntegrationWarning(warning);
    });
  }

  private handleTelemetryIntegrationWarning(warning: TelemetryIntegrationWarning) {
    console.warn(`[telemetry_integration:${warning.provider}] ${warning.message}`, warning);
    this.emitPublicEvent("telemetry_integration_warning", warning);
  }

  private async dispatchTelemetryIntegration(reason: TelemetryIntegrationDispatchReason) {
    if (!this.telemetryIntegration.isEnabled()) {
      return;
    }

    try {
      const audit = await this.getDebugAudit();
      if (!audit) {
        return;
      }
      await this.telemetryIntegration.dispatchAudit(audit, reason);
    } catch (error) {
      this.handleTelemetryIntegrationWarning({
        provider: "none",
        code: "dispatch_failed",
        message: "Failed to dispatch audit payload through telemetry integration.",
        timestamp: Date.now(),
        detail: {
          reason,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      });
    }
  }

  private handleSttAuditIngestionWarning(warning: SttAuditIngestionWarning) {
    console.warn(`[stt_audit_ingestion:${warning.code}] ${warning.message}`, warning);
    this.emitPublicEvent("stt_audit_ingestion_warning", warning);
  }

  private async dispatchSttAuditIngestion() {
    if (!this.transcriptionService?.ingestSttAudit) {
      return;
    }

    try {
      const audit = await this.getDebugAudit();
      if (!audit) {
        return;
      }

      const warning = await this.transcriptionService.ingestSttAudit(audit);
      if (warning) {
        this.handleSttAuditIngestionWarning(warning);
      }
    } catch (error) {
      this.handleSttAuditIngestionWarning({
        code: "dispatch_failed",
        message: "Failed to dispatch STT audit ingestion request.",
        detail: {
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      });
    }
  }

  private buildInMemoryDebugAuditState(): DebugAuditState | null {
    if (!this.debugAudit) {
      return null;
    }

    return {
      enabled: this.debugAudit.enabled,
      advancedMetrics: this.debugAudit.advancedMetrics,
      autoDownload: this.debugAudit.autoDownload,
      fileName: this.debugAudit.fileName,
      label: this.debugAudit.label,
      metadata: this.debugAudit.metadata,
      sessionId: this.debugAudit.sessionId,
      createdAt: this.debugAudit.createdAt,
      startedAt: this.debugAudit.startedAt,
      stoppedAt: this.debugAudit.stoppedAt,
      requestedProvider: this.debugAudit.requestedProvider,
      resolvedProvider: this.debugAudit.resolvedProvider,
      phases: this.debugAudit.phases.map((phase) => ({ ...phase })),
      incidents: this.debugAudit.incidents.map((incident) => ({ ...incident })),
      transcriptMilestones: this.debugAudit.transcriptMilestones.map((milestone) => ({
        ...milestone,
      })),
      checkpoints: this.debugAudit.checkpoints.map((checkpoint) => ({
        ...checkpoint,
        resilienceStatus: cloneResilienceStatus(checkpoint.resilienceStatus),
        metricsSnapshot: cloneMetricsSnapshot(checkpoint.metricsSnapshot),
      })),
      finalDigests: this.debugAudit.finalDigests.map((digest) => ({ ...digest })),
    };
  }

  private emitPublicEvent(eventName: string, detail?: unknown) {
    this.recordDebugEvent(eventName, detail);
    this.emit(eventName, detail);

    if (eventName === "error") {
      void this.dispatchTelemetryIntegration("error");
      return;
    }

    if (eventName === "disconnected") {
      void this.dispatchTelemetryIntegration("disconnected");
    }
  }

  private recordDebugEvent(eventName: string, detail?: unknown) {
    if (!this.debugAudit) {
      return;
    }

    const timestamp = Date.now();
    const serializedDetail = this.serializeDebugDetail(detail);

    if (eventName === "ready") {
      this.appendDebugPhase("ready", timestamp);
      this.persistDebugCheckpoint(timestamp, true);
      return;
    }

    if (eventName === "connected") {
      this.appendDebugPhase("connected", timestamp);
      this.persistDebugCheckpoint(timestamp, true);
      return;
    }

    if (eventName === "disconnected") {
      this.appendDebugPhase("disconnected", timestamp);
      this.persistDebugCheckpoint(timestamp, true);
      return;
    }

    if (eventName === "reconnecting") {
      this.appendDebugPhase("reconnecting", timestamp);
      this.persistDebugCheckpoint(timestamp, true);
      return;
    }

    if (eventName === "reconnected") {
      this.appendDebugPhase("reconnected", timestamp);
      this.persistDebugCheckpoint(timestamp, true);
      return;
    }

    if (eventName === "error") {
      this.appendDebugIncident(
        {
          type: this.isFatalError(serializedDetail) ? "fatal_error" : "error",
          timestamp,
          endedAt: null,
          detail: serializedDetail,
        }
      );
      this.persistDebugCheckpoint(timestamp, true);
      return;
    }

    if (eventName === "recognizing" && typeof detail === "string") {
      this.recordDebugMilestone("first_partial", detail, timestamp);
      this.persistDebugCheckpoint(timestamp, false);
      return;
    }

    if (eventName === "recognized" && typeof detail === "string") {
      this.recordDebugMilestone("first_final", detail, timestamp);
      this.recordDebugMilestone("last_final", detail, timestamp);
      this.appendDebugFinalDigest(detail, timestamp);
      this.persistDebugCheckpoint(timestamp, false);
      return;
    }

    if (eventName === "recognized_diarization") {
      this.recordDebugMilestone("first_diarization", null, timestamp);
      this.persistDebugCheckpoint(timestamp, false);
      return;
    }

    if (
      eventName === "resilience_status" &&
      serializedDetail &&
      typeof serializedDetail === "object"
    ) {
      this.recordDebugStatus(serializedDetail as Record<string, unknown>, timestamp);
      return;
    }

    if (
      eventName === "telemetry" &&
      serializedDetail &&
      typeof serializedDetail === "object"
    ) {
      this.recordDebugTelemetry(serializedDetail as TelemetrySnapshot, timestamp);
    }
  }

  private recordDebugStatus(detail: Record<string, unknown>, timestamp: number) {
    if (!this.debugAudit) {
      return;
    }

    const nextStatus = normalizeResilienceStatus(detail, timestamp);
    const previousStatus = this.debugAudit.lastResilienceStatus;

    if (previousStatus?.browserOnline === true && nextStatus.browserOnline === false) {
      this.appendDebugIncident(
        {
          type: "offline",
          timestamp,
          endedAt: null,
          detail: { browserOnline: false },
        }
      );
    }

    if (previousStatus?.browserOnline === false && nextStatus.browserOnline === true) {
      this.appendDebugIncident(
        {
          type: "online",
          timestamp,
          endedAt: null,
          detail: { browserOnline: true },
        }
      );
    }

    if (!previousStatus?.isBufferingAudio && nextStatus.isBufferingAudio) {
      this.appendDebugIncident(
        {
          type: "buffering_started",
          timestamp,
          endedAt: null,
          detail: {
            totalBufferedAudioBytes: nextStatus.totalBufferedAudioBytes ?? null,
          },
        }
      );
    }

    if (previousStatus?.isBufferingAudio && !nextStatus.isBufferingAudio) {
      this.appendDebugIncident(
        {
          type: "buffering_cleared",
          timestamp,
          endedAt: null,
          detail: {
            totalBufferedAudioBytes: nextStatus.totalBufferedAudioBytes ?? null,
          },
        }
      );
    }

    if (!previousStatus?.isDrainingAudio && nextStatus.isDrainingAudio) {
      this.appendDebugIncident(
        {
          type: "drain_started",
          timestamp,
          endedAt: null,
          detail: { drainExitReason: nextStatus.drainExitReason ?? null },
        }
      );
    }

    if (
      previousStatus?.isDrainingAudio &&
      !nextStatus.isDrainingAudio &&
      (previousStatus.drainExitReason === "completed" ||
        previousStatus.finishDeliveryState === "sent")
    ) {
      this.appendDebugIncident(
        {
          type: "drain_completed",
          timestamp,
          endedAt: null,
          detail: {
            drainExitReason: previousStatus.drainExitReason ?? null,
            finishDeliveryState: previousStatus.finishDeliveryState ?? null,
          },
        }
      );
    }

    if (
      (nextStatus.drainExitReason === "stalled" &&
        previousStatus?.drainExitReason !== "stalled") ||
      (nextStatus.finishDeliveryState === "timed_out" &&
        previousStatus?.finishDeliveryState !== "timed_out")
    ) {
      this.appendDebugIncident(
        {
          type: "drain_stalled",
          timestamp,
          endedAt: null,
          detail: {
            drainExitReason: nextStatus.drainExitReason ?? null,
            finishDeliveryState: nextStatus.finishDeliveryState ?? null,
          },
        }
      );
    }

    if (
      nextStatus.connectionState === "disconnected" &&
      previousStatus?.connectionState !== "disconnected"
    ) {
      this.appendDebugIncident(
        {
          type: "terminal_disconnect",
          timestamp,
          endedAt: null,
          detail: {
            connectionState: nextStatus.connectionState ?? null,
            lastDisconnect: nextStatus.lastDisconnect ?? null,
          },
        }
      );
    }

    const important =
      previousStatus?.connectionState !== nextStatus.connectionState ||
      previousStatus?.browserOnline !== nextStatus.browserOnline ||
      previousStatus?.isBufferingAudio !== nextStatus.isBufferingAudio ||
      previousStatus?.isDrainingAudio !== nextStatus.isDrainingAudio ||
      previousStatus?.drainExitReason !== nextStatus.drainExitReason ||
      previousStatus?.finishDeliveryState !== nextStatus.finishDeliveryState;

    this.debugAudit.lastResilienceStatus = nextStatus;
    this.persistDebugCheckpoint(timestamp, important);
  }

  private recordDebugTelemetry(
    telemetrySnapshot: TelemetrySnapshot,
    timestamp: number
  ) {
    if (!this.debugAudit) {
      return;
    }

    this.debugAudit.lastMetricsSnapshot = cloneMetricsSnapshot(
      telemetryToLegacyMetricsSnapshot(telemetrySnapshot)
    );
    this.persistDebugCheckpoint(timestamp, false);
  }

  private serializeDebugDetail(detail: unknown) {
    if (detail instanceof Error) {
      return {
        name: detail.name,
        message: detail.message,
      };
    }

    if (detail === undefined) {
      return null;
    }

    if (detail && typeof detail === "object") {
      try {
        return JSON.parse(JSON.stringify(detail));
      } catch {
        return {
          message: String(detail),
        };
      }
    }

    return detail;
  }

  private setResolvedDebugProvider(provider: Providers) {
    if (!this.debugAudit) {
      return;
    }

    this.debugAudit.resolvedProvider = provider;
    const sessionId = this.debugAudit.sessionId;
    this.queueDebugAuditWrite(() =>
      this.debugAuditStore.updateSession(sessionId, {
        resolvedProvider: provider,
      })
    );
  }

  private markDebugStart() {
    if (!this.debugAudit || this.debugAudit.startedAt) {
      return;
    }

    const timestamp = Date.now();
    this.debugAudit.startedAt = timestamp;
    const sessionId = this.debugAudit.sessionId;
    this.queueDebugAuditWrite(() =>
      this.debugAuditStore.updateSession(sessionId, {
        startedAt: timestamp,
      })
    );
    this.appendDebugPhase("started", timestamp);
    this.persistDebugCheckpoint(timestamp, true);
  }

  private markDebugStop() {
    if (!this.debugAudit || this.debugAudit.stoppedAt) {
      return;
    }

    const timestamp = Date.now();
    this.debugAudit.stoppedAt = timestamp;
    const sessionId = this.debugAudit.sessionId;
    this.queueDebugAuditWrite(() =>
      this.debugAuditStore.updateSession(sessionId, {
        stoppedAt: timestamp,
      })
    );
    this.appendDebugPhase("stopped", timestamp);
    this.persistDebugCheckpoint(timestamp, true);
  }

  private generateDebugSessionId() {
    return [
      "sofya-debug",
      Date.now().toString(36),
      Math.random().toString(36).slice(2, 8),
    ].join("-");
  }

  private getDebugFileName(overrideFileName?: string) {
    const requestedName = overrideFileName ?? this.debugAudit?.fileName ?? null;

    if (requestedName) {
      return requestedName.endsWith(".json")
        ? requestedName
        : `${requestedName}.json`;
    }

    const labelSegment =
      this.debugAudit?.label
        ?.toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") ?? "";

    if (labelSegment) {
      return `sofya-debug-audit-${labelSegment}.json`;
    }

    return `${this.debugAudit?.sessionId ?? "sofya-debug-audit"}.json`;
  }

  private prepareDebugSessionForStart() {
    if (!this.debugAudit) {
      return;
    }

    if (this.debugAudit.startedAt !== null && this.debugAudit.stoppedAt === null) {
      return;
    }

    if (this.debugAudit.startedAt !== null && this.debugAudit.stoppedAt !== null) {
      this.resetDebugAuditSession(this.debugAudit.sessionId, true);
    }
  }

  private resetDebugAuditSession(
    previousSessionId: string,
    clearPreviousSession: boolean
  ) {
    if (!this.debugAudit) {
      return;
    }

    const nextState = this.buildLiveDebugAuditState(this.debugAudit, {
      requestedProvider: this.debugAudit.requestedProvider,
      resolvedProvider: this.debugAudit.resolvedProvider,
    });

    this.debugAudit = nextState;

    if (clearPreviousSession) {
      this.queueDebugAuditWrite(() => this.debugAuditStore.clear(previousSessionId));
    }

    this.initializeDebugAuditSession(nextState);

    if (this.transcriptionService) {
      this.appendDebugPhase("ready", nextState.createdAt);
    }
  }

  private initializeDebugAuditSession(state: LiveDebugAuditState) {
    this.queueDebugAuditWrite(() =>
      this.debugAuditStore.createSession({
        enabled: state.enabled,
        advancedMetrics: state.advancedMetrics,
        autoDownload: state.autoDownload,
        fileName: state.fileName,
        label: state.label,
        metadata: state.metadata,
        sessionId: state.sessionId,
        createdAt: state.createdAt,
        startedAt: state.startedAt,
        stoppedAt: state.stoppedAt,
        requestedProvider: state.requestedProvider,
        resolvedProvider: state.resolvedProvider,
      })
    );
  }

  private appendDebugPhase(type: SessionReportPhase["type"], timestamp: number) {
    if (!this.debugAudit) {
      return;
    }

    const phase: SessionReportPhase = { type, timestamp };
    this.pushDebugItem(this.debugAudit.phases, phase, LIVE_AUDIT_PHASE_LIMIT);

    const sessionId = this.debugAudit.sessionId;
    const sequence = ++this.debugAudit.phaseSequence;
    this.queueDebugAuditWrite(() =>
      this.debugAuditStore.appendPhase(sessionId, sequence, phase)
    );
  }

  private appendDebugIncident(
    incident: SessionReportIncident
  ) {
    if (!this.debugAudit) {
      return;
    }

    this.pushDebugItem(this.debugAudit.incidents, incident, LIVE_AUDIT_INCIDENT_LIMIT);

    const sessionId = this.debugAudit.sessionId;
    const sequence = ++this.debugAudit.incidentSequence;
    this.queueDebugAuditWrite(() =>
      this.debugAuditStore.appendIncident(sessionId, sequence, incident)
    );
  }

  private recordDebugMilestone(
    type: SessionReportTranscriptMilestone["type"],
    text: string | null,
    timestamp: number
  ) {
    if (!this.debugAudit) {
      return;
    }

    if (type === "first_partial" && this.debugAudit.milestones.firstPartial) {
      return;
    }

    if (type === "first_final" && this.debugAudit.milestones.firstFinal) {
      return;
    }

    if (type === "first_diarization" && this.debugAudit.milestones.firstDiarization) {
      return;
    }

    const milestone: SessionReportTranscriptMilestone = {
      type,
      timestamp,
      text,
    };
    const sessionId = this.debugAudit.sessionId;

    if (type === "first_partial") {
      this.debugAudit.milestones.firstPartial = milestone;
    } else if (type === "first_final") {
      this.debugAudit.milestones.firstFinal = milestone;
    } else if (type === "last_final") {
      this.debugAudit.milestones.lastFinal = milestone;
    } else if (type === "first_diarization") {
      this.debugAudit.milestones.firstDiarization = milestone;
    }
    const existingMilestoneIndex = this.debugAudit.transcriptMilestones.findIndex(
      (entry) => entry.type === type
    );
    if (existingMilestoneIndex >= 0) {
      this.debugAudit.transcriptMilestones[existingMilestoneIndex] = milestone;
    } else {
      this.pushDebugItem(
        this.debugAudit.transcriptMilestones,
        milestone,
        LIVE_AUDIT_MILESTONE_LIMIT
      );
    }

    this.queueDebugAuditWrite(() =>
      this.debugAuditStore.putMilestone(sessionId, milestone)
    );
  }

  private appendDebugFinalDigest(text: string, timestamp: number) {
    if (!this.debugAudit) {
      return;
    }

    const sessionId = this.debugAudit.sessionId;
    const sequence = ++this.debugAudit.finalDigestSequence;
    const digest = {
      digest: this.hashDebugText(text),
      timestamp,
    };
    this.pushDebugItem(this.debugAudit.finalDigests, digest, LIVE_AUDIT_DIGEST_LIMIT);
    this.queueDebugAuditWrite(() =>
      this.debugAuditStore.appendFinalDigest(sessionId, sequence, digest)
    );
  }

  private persistDebugCheckpoint(timestamp: number, force: boolean) {
    if (!this.debugAudit) {
      return;
    }

    if (
      !force &&
      this.debugAudit.lastCheckpointAt !== null &&
      timestamp - this.debugAudit.lastCheckpointAt < DEBUG_CHECKPOINT_INTERVAL_MS
    ) {
      return;
    }

    const checkpoint = {
      timestamp,
      resilienceStatus: cloneResilienceStatus(this.debugAudit.lastResilienceStatus),
      metricsSnapshot: cloneMetricsSnapshot(this.debugAudit.lastMetricsSnapshot),
    };
    const sessionId = this.debugAudit.sessionId;
    const sequence = ++this.debugAudit.checkpointSequence;
    this.debugAudit.lastCheckpointAt = timestamp;
    this.pushDebugItem(this.debugAudit.checkpoints, checkpoint, LIVE_AUDIT_CHECKPOINT_LIMIT);
    this.queueDebugAuditWrite(() =>
      this.debugAuditStore.appendCheckpoint(sessionId, sequence, checkpoint)
    );
  }

  private pushDebugItem<T>(target: T[], value: T, limit: number) {
    target.push(value);
    if (target.length <= limit) {
      return;
    }

    target.splice(0, target.length - limit);
  }

  private queueDebugAuditWrite(operation: () => Promise<void>) {
    if (!this.debugAudit?.enabled) {
      return;
    }

    this.debugAuditWritePromise = this.debugAuditWritePromise
      .then(operation)
      .catch((error) => {
        console.warn("Failed to persist debug audit data.", error);
      });
  }

  private async flushDebugAuditWrites() {
    await this.debugAuditWritePromise;
  }

  private isFatalError(detail: unknown): boolean {
    if (detail && typeof detail === "object") {
      const detailRecord = detail as Record<string, unknown>;
      const message = detailRecord.message;
      const code = detailRecord.code;

      return (
        (typeof message === "string" && /fatal/i.test(message)) ||
        (typeof code === "string" && /fatal/i.test(code))
      );
    }

    return typeof detail === "string" ? /fatal/i.test(detail) : false;
  }

  private hashDebugText(text: string): string {
    let hash = 2166136261;

    for (let index = 0; index < text.length; index += 1) {
      hash ^= text.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return `${text.length}:${(hash >>> 0).toString(16)}`;
  }

  private resolveCurrentDebugStatus(): SessionReportResilienceStatus | null {
    const serviceStatus =
      (this.transcriptionService?.getResilienceStatus?.() as
        | SessionReportResilienceStatus
        | null) ?? null;

    if (!serviceStatus) {
      return this.debugAudit?.lastResilienceStatus ?? null;
    }

    const liveStatus = this.debugAudit?.lastResilienceStatus ?? null;

    if (!liveStatus) {
      return serviceStatus;
    }

    const serviceTimestamp =
      typeof serviceStatus.timestamp === "number" ? serviceStatus.timestamp : null;
    const liveTimestamp =
      typeof liveStatus.timestamp === "number" ? liveStatus.timestamp : null;

    if (serviceTimestamp === null) {
      return liveStatus;
    }

    if (liveTimestamp !== null && liveTimestamp > serviceTimestamp) {
      return liveStatus;
    }

    return serviceStatus;
  }
}

export const createTranscriber = (connection: Connection) =>
  new SofyaTranscriber(connection);
